import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { getMerchBaseUrl, resolveApiUrl } from "../../../utils/apiBase";

const configuredMerchHome = (
  process.env.REACT_APP_MERCH_URL_HOME ||
  process.env.REACT_APP_MERCH_URL ||
  "https://danzflo.com"
)
  .replace(/\/merch(?=\/|$)/gi, "")
  .replace(/\/+$/, "");

const socialUrl =
  process.env.REACT_APP_SOCIAL_API_URL || `${configuredMerchHome}/social`;
const musicUrl =
  process.env.REACT_APP_MUSIC_API_URL || `${configuredMerchHome}/#music`;

const FIND_LINKS = [
  { label: "Events", to: "/" },
  { label: "Wishlist", to: "/wishlist" },
  { label: "My Events", to: "/MyEvents" },
];

const EXPLORE_LINKS = [
  { label: "Music", href: musicUrl },
  { label: "TV", platform: "tv", href: process.env.REACT_APP_TV_API_URL },
  { label: "Social", href: socialUrl },
  {
    label: "Classes",
    platform: "class",
    href: process.env.REACT_APP_CLASSES_URL,
  },
  { label: "Home", href: configuredMerchHome },
];

const siteUrl = (url = "") => {
  const raw = String(url || "").trim();
  if (!raw) return "";
  return resolveApiUrl(raw).replace(/\/+$/, "");
};

const usableAuthKey = (masterToken) => {
  const key = String(masterToken || "").trim();
  if (!key) return "";
  const lower = key.toLowerCase();
  if (lower === "undefined" || lower === "null" || lower === "false") return "";
  return key;
};

/**
 * Classes and TV do not share the Event session.
 * Logged-in users must hit the SSO entry (same handoff as the top nav).
 * A bare /classes URL is permission-gated and dumps people on /classes/access.
 */
function platformHref(link, platform, masterToken) {
  const merchBase = (getMerchBaseUrl() || configuredMerchHome).replace(
    /\/+$/,
    ""
  );
  const base =
    siteUrl(link) ||
    (platform === "class" ? `${merchBase}/classes` : `${merchBase}/tv`);
  const authKey = usableAuthKey(masterToken);

  if (platform === "class") {
    if (!authKey) {
      const login = new URL("/login", `${merchBase}/`);
      login.searchParams.set("platform", "classes");
      login.searchParams.set("redirect", `${base}/`);
      return login.toString();
    }
    return `${base}/auth/${authKey}`;
  }

  if (platform === "tv") {
    if (!authKey) return base;
    // Query values treat "+" as a space unless encoded. Auth keys are base64.
    return `${base}/index.php?link1=external_auth&authId=${encodeURIComponent(authKey)}`;
  }

  return base;
}

const INFO_LINKS = [
  { label: "How Does It Work", href: `${configuredMerchHome}/how-it-works` },
  // { label: "Policies & Rules", href: `${configuredMerchHome}/privacy-policy` },
  // { label: "Information", href: `${configuredMerchHome}/how-does-it-work` },
];

const POLICY_LINKS = [
  { label: "Privacy Policy", href: `${configuredMerchHome}/privacy-policy` },
  { label: "Terms & Conditions", href: `${configuredMerchHome}/terms-and-conditions` },
  { label: "Cookie Policy", href: `${configuredMerchHome}/cookie-policy` },
];

function FooterColumn({ title, children }) {
  return (
    <div className="portal-footer-col">
      <div className="footer-eyebrow">{title}</div>
      {children}
    </div>
  );
}

function FooterLink({ label, to, href }) {
  if (to) {
    return (
      <Link to={to}>{label}</Link>
    );
  }
  return <a href={href}>{label}</a>;
}

export default function EventFooter() {
  const masterToken = useSelector((state) => state.user?.masterToken);
  const exploreLinks = EXPLORE_LINKS.map((link) =>
    link.platform
      ? {
          ...link,
          href: platformHref(link.href, link.platform, masterToken),
        }
      : link
  );

  return (
    <footer className="footer footer-clean footer-dashboard portal-footer event-portal-footer">
      <div className="container portal-footer-container">
        <div className="portal-footer-grid">
          <div className="portal-footer-brand">
            <div className="portal-footer-logo mb-3">
              <img
                className="brand-logo"
                src="/event/assets/img/logo.png"
                alt="Danzflo"
              />
            </div>
            <p className="footer-lead mb-3">
              Find it fast. Book smarter. Stay inspired.
            </p>

            <div className="footer-addr mt-3">
              <div className="footer-eyebrow">Address</div>
              <div className="footer-muted mt-2">
                <i className="bi bi-geo-alt me-1" aria-hidden="true" />
                4545 West Sahara ave, Las Vegas, NV
              </div>
            </div>
          </div>

          <FooterColumn title="Find it Fast">
            <ul className="list-unstyled footer-links mt-3 mb-0">
              {FIND_LINKS.map((link) => (
                <li key={link.label}>
                  <FooterLink {...link} />
                </li>
              ))}
            </ul>
          </FooterColumn>

          <FooterColumn title="Explore">
            <ul className="list-unstyled footer-links mt-3 mb-0">
              {exploreLinks.map((link) => (
                <li key={link.label}>
                  <FooterLink {...link} />
                </li>
              ))}
            </ul>
          </FooterColumn>

          <FooterColumn title="Information">
            <ul className="list-unstyled footer-links mt-3 mb-0">
              {INFO_LINKS.map((link) => (
                <li key={link.label}>
                  <FooterLink {...link} />
                </li>
              ))}
            </ul>
          </FooterColumn>

          <FooterColumn title="Policies">
            <ul className="list-unstyled footer-links mt-3 mb-0">
              {POLICY_LINKS.map((link) => (
                <li key={link.label}>
                  <FooterLink {...link} />
                </li>
              ))}
            </ul>
          </FooterColumn>
        </div>

        <div className="footer-bar portal-footer-bar">
          <small className="footer-muted portal-footer-copy">
            © {new Date().getFullYear()} Danzflo. All rights reserved.
          </small>
          <small className="footer-muted portal-footer-credit">
            Designed and developed by Aresourcepool.com
          </small>
        </div>
      </div>
    </footer>
  );
}
